import { useState, useEffect } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useJob } from '../../state';
import { Loading } from '../loading';
import { StateBadge } from '../badge';
import { prettyBytes, elapsedFromTimer } from '../../util';
import { styles } from '../../constants';

const activeBackground = '#eee';

const flexCenter = {
  display: 'flex',
  alignItems: 'center',
};

const RunningElapsed = () => {
  const [start, setStart] = useState(new Date().getTime());
  const [end, setEnd] = useState(new Date().getTime());

  useEffect(() => {
    const id = setInterval(() => {
      setEnd(new Date().getTime());
    }, 30);
    return () => clearInterval(id);
  }, []);

  return <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{((end - start) / 1000).toFixed(2)}&nbsp;sec</div>;
};

const DoneElapsed = ({ job }) => {
  const elapsed = elapsedFromTimer(job?.timer);

  if (!elapsed || isNaN(elapsed)) {
    return null;
  }

  return <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{(elapsed / 1000).toFixed(1)}&nbsp;sec</div>;
};

const Elapsed = ({ job }) => {
  console.log('Elapsed:', job);

  if (job.state == 'active') {
    return <RunningElapsed />;
  } else if (['completed', 'failed'].includes(job.state)) {
    return <DoneElapsed job={job} />;
  } else {
    return null;
  }
};

const VisitCard = ({ job, open, onClick }) => {
  let body;

  if (open) {
    const nodes = (job.results || []).map((it) => {
      const nodes2 = [];
      const types = [
        ['html', 'HTML'],
        ['markdown', 'Markdown'],
        ['slimHtml', 'Slim HTML'],
        ['text', 'Text'],
      ];
      for (const [type, pretty] of types) {
        if (!it[type]) {
          continue;
        }
        const { bytes, link } = it[type];
        nodes2.push(
          <div key={type}>
            <a href={link} target="_blank" style={{ color: 'unset' }}>
              {prettyBytes(bytes)} {pretty} <FaExternalLinkAlt size={10} />
            </a>
          </div>,
        );
      }
      return (
        <div style={{ display: 'flex', gap: 5 }}>
          <div>{nodes2}</div>
        </div>
      );
    });

    body = (
      <div style={{ paddingTop: 5 }}>
        {nodes}
        {/*
        {JSON.stringify(job?.timer, null, 2)}
        */}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', background: open ? activeBackground : '' }}>
      <div
        onClickx={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursorx: 'pointer',
          top: 0,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <StateBadge state={job.state} />
          <div>
            Visit <b>{job.params.url}</b>
          </div>
        </div>
        <Elapsed job={job} />
      </div>
      {body}
    </div>
  );
};

const RunCompletionCard = ({ job, open, onClick }) => {
  let body;

  const partial = job.answer?.partial || '';
  const str = typeof partial == 'string' ? partial : JSON.stringify(partial);
  if (open) {
    body = <div style={{ overflowWrap: 'anywhere' }}>{str}</div>;
  }

  const preview = (
    <div style={{ display: 'flex', alignItems: 'center', position: 'relative', borderx: '1px solid orange' }}>
      <div style={{ ...styles.textOverflowStyle, width: '50%' }}>{str}</div>
      <div
        style={{ ...styles.textOverflowStyle, width: '50%', position: 'absolute', right: 0, top: 0, direction: 'rtl' }}
      >
        {str}
      </div>
    </div>
  );

  return (
    <div style={{ position: 'relative', width: '100%', background: open ? activeBackground : '' }}>
      <div
        onClickx={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursorx: 'pointer',
          position: 'sticky',
          top: 0,
          borderx: '1px solid red',
          width: '100%',
        }}
      >
        <div style={{ ...flexCenter, gap: 5 }}>
          <div style={{ borderx: '1px solid blue', widthx: 20 }}>
            <StateBadge state={job.state} />
          </div>
          <div style={{ borderx: '1px solid blue', width: 60 }}>
            <b>Ask AI</b>
          </div>
          <div style={{ borderx: '1px solid blue', width: 440 }}>{preview}</div>
        </div>
        <Elapsed job={job} />
      </div>
      {body}
    </div>
  );
};

export const JobCard = ({ jobId }) => {
  const [hover, setHover] = useState();
  const [open, setOpen] = useState();
  const { job } = useJob(jobId);

  let body;
  if (!job.method) {
    body = <Loading size={13} />;
  } else {
    body = {
      visit: <VisitCard job={job} open={open} onClick={() => setOpen(!open)} />,
      runCompletion: <RunCompletionCard job={job} open={open} onClick={() => setOpen(!open)} />,
    }[job.method] || <div>{job.method}</div>;
  }

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '2px 4px',
        borderRadius: 5,
        fontSize: 13,
        borderx: '1px solid green',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        ...(hover || open ? { background: activeBackground } : {}),
      }}
    >
      {body}

      {/*
      JobID:{jobId}
      <pre>{JSON.stringify(job, null, 2)}</pre>
      */}
    </div>
  );
};
